/**
 * PremiumEmptyState - Componente genérico de estado vazio
 * Reutilizável em todas as telas CRUD
 *
 * Props:
 * - icon: React Component (ícone principal - componente, não elemento)
 * - title: string (título quando sem filtro)
 * - titleFiltered: string (título quando com filtro)
 * - subtitle: string (subtítulo quando sem filtro)
 * - subtitleFiltered: string (subtítulo quando com filtro)
 * - addPath: string (path para navegação do botão adicionar)
 * - addLabel: string (texto do botão adicionar)
 * - hasFilter / hasFilters: boolean (indica se há filtro ativo - suporta ambos)
 * - onClearFilter / onClearFilters: function (callback para limpar filtros - suporta ambos)
 * - showAddButton: boolean (mostrar botão de adicionar)
 * - disabled: boolean (desabilita o botão de adicionar)
 */

import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import InboxIcon from '@mui/icons-material/Inbox';
import { useNavigate } from 'react-router-dom';
import { usePremiumTokens } from '../../theme/usePremiumTokens';
import { useTranslation } from '../LocalizationProvider';

const PremiumEmptyState = ({
  icon: IconComponent,
  title,
  titleFiltered,
  subtitle,
  subtitleFiltered,
  addPath,
  addLabel,
  hasFilter,
  hasFilters,
  onClearFilter,
  onClearFilters,
  showAddButton = true,
  disabled = false,
}) => {
  const t = useTranslation();
  const navigate = useNavigate();
  const tokens = usePremiumTokens();

  // Suporta ambas as variações de props
  const isFiltered = hasFilter || hasFilters;
  const handleClearFilter = onClearFilter || onClearFilters;

  // Ícone padrão se não fornecido
  const DefaultIcon = IconComponent || InboxIcon;

  // Textos padrão
  const displayTitle = isFiltered
    ? (titleFiltered || t('sharedNoResults') || 'Nenhum resultado encontrado')
    : (title || t('sharedNoData') || 'Nenhum registro encontrado');

  const displaySubtitle = isFiltered
    ? (subtitleFiltered || t('sharedTryDifferentFilter') || 'Tente ajustar os filtros de busca')
    : (subtitle || t('sharedAddFirst') || 'Adicione seu primeiro registro');

  const displayAddLabel = addLabel || t('sharedAdd') || 'Adicionar';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
        px: 2,
        textAlign: 'center',
      }}
    >
      {/* Ícone */}
      {isFiltered ? (
        <SearchOffIcon
          sx={{
            fontSize: 64,
            color: tokens?.text?.muted || '#6e7681',
            mb: 2,
          }}
        />
      ) : (
        <DefaultIcon
          sx={{
            fontSize: 64,
            color: tokens?.text?.muted || '#6e7681',
            mb: 2,
          }}
        />
      )}

      {/* Título */}
      <Typography
        variant="h6"
        sx={{
          color: tokens?.text?.secondary || '#8b949e',
          mb: 1,
        }}
      >
        {displayTitle}
      </Typography>

      {/* Subtítulo */}
      <Typography
        variant="body2"
        sx={{
          color: tokens?.text?.muted || '#6e7681',
          mb: 3,
          maxWidth: 400,
        }}
      >
        {displaySubtitle}
      </Typography>

      {/* Botões */}
      {isFiltered && handleClearFilter ? (
        <Button
          variant="outlined"
          onClick={handleClearFilter}
          sx={{
            borderColor: tokens?.accent?.primary || '#00d4aa',
            color: tokens?.accent?.primary || '#00d4aa',
            '&:hover': {
              backgroundColor: tokens?.accent?.subtle || 'rgba(0, 212, 170, 0.1)',
              borderColor: tokens?.accent?.primary || '#00d4aa',
            },
          }}
        >
          {t('sharedClearFilters') || 'Limpar Filtros'}
        </Button>
      ) : (
        showAddButton && addPath && !disabled && (
          <Button
            variant="contained"
            startIcon={<AddCircleIcon />}
            onClick={() => navigate(addPath)}
            sx={{
              backgroundColor: tokens?.accent?.primary || '#00d4aa',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: tokens?.accent?.hover || '#00e4ba',
              },
            }}
          >
            {displayAddLabel}
          </Button>
        )
      )}
    </Box>
  );
};

export default React.memo(PremiumEmptyState);
